import { useEffect, useMemo, useState } from 'react';
// Imported under a non-`use` name on purpose. Despite the filename, the default
// export is a plain FACTORY that builds an axios instance — it calls no hooks
// and has no hook semantics. Binding it as `useAxiosAuth` makes
// react-hooks/rules-of-hooks treat every call site as a misplaced hook, which is
// why useTenantFeatures.js reports an error it cannot really fix. The rule keys
// on the identifier, so the accurate name is also the quiet one.
import createAuthedAxios from './useAxiosAuth';
import baseURL from '../utils/baseURL';

// What the signed-in user may do.
//
// Mirrors useTenantFeatures in shape and for the same reasons: seeded from
// localStorage so the first render already has the right menu, refreshed from
// the network immediately after. A sidebar that appears and then re-arranges a
// moment later reads as a glitch.
//
// IT DIFFERS IN ONE IMPORTANT WAY. useTenantFeatures FAILS OPEN — if the lookup
// fails it shows everything, because hiding a menu is cosmetic and the server
// still refuses the request. This one FAILS CLOSED for staff: a receptionist
// shown a pharmacy menu they cannot use is not a cosmetic problem, it is a
// screenful of buttons that all return 403. Admins are unaffected: they hold
// `allowAll` and never depend on this resolving at all.
//
// SECURITY: none of this is trusted. Every route resolves the permission
// server-side from the database (api/services/staffPermissionService.js), so
// editing localStorage changes what the UI draws and nothing else.

const CACHE_KEY = 'StaffPermissions';
const ROLE_KEY = 'StaffRoleName';

const readCache = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

/** Write what login returned, so the first paint after signing in is correct. */
export const cacheStaffAccess = (staff) => {
  try {
    if (!staff) {
      localStorage.removeItem(CACHE_KEY);
      localStorage.removeItem(ROLE_KEY);
      return;
    }
    localStorage.setItem(CACHE_KEY, JSON.stringify(staff.permissions || {}));
    localStorage.setItem(ROLE_KEY, staff.roleName || '');
  } catch {
    // A full localStorage must not break login.
  }
};

export default function usePermissions() {
  const role = (localStorage.getItem('Role') || '').toLowerCase();
  // admin and super_admin are not staff and hold no sub-role. They run the
  // hospital — and are the ones who grant these permissions — so gating them on
  // one would be circular. The backend takes the same position.
  const allowAll = role === 'admin' || role === 'super_admin';

  const [permissions, setPermissions] = useState(() => readCache() || {});
  const [roleName, setRoleName] = useState(() => localStorage.getItem(ROLE_KEY) || '');
  // `ready` is false only while a staff member's first fetch is in flight, so a
  // guard can wait rather than briefly rendering a locked screen to somebody who
  // is in fact allowed in.
  const [ready, setReady] = useState(() => allowAll || readCache() !== null);

  // Built once rather than per request: the instance reads the access token in
  // its request interceptor, so a single memoised copy stays correct across a
  // token refresh.
  const axiosInstance = useMemo(() => createAuthedAxios(), []);

  useEffect(() => {
    if (allowAll) { setReady(true); return; }
    if (localStorage.getItem('isAuthenticated') !== 'true') { setReady(true); return; }

    let cancelled = false;
    (async () => {
      try {
        const res = await axiosInstance.get(`${baseURL}/user-roles/me`);
        if (cancelled) return;
        const next = res?.data?.permissions || {};
        setPermissions(next);
        setRoleName(res?.data?.roleName || '');
        localStorage.setItem(CACHE_KEY, JSON.stringify(next));
        localStorage.setItem(ROLE_KEY, res?.data?.roleName || '');
      } catch {
        // Keep the cached set. Failing closed here would blank the menu of
        // somebody mid-shift because of one dropped request; the cache is at
        // most a few minutes stale and the server is still the real gate.
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const can = (permissionId) => {
    if (allowAll) return true;
    if (!permissionId) return false;
    return permissions[permissionId] === true;
  };

  const canAny = (ids = []) => (allowAll ? true : ids.some((id) => permissions[id] === true));

  return { can, canAny, permissions, roleName, allowAll, ready, isStaff: role === 'user' };
}
